import type {
  AddressDto,
  CreateAccountDto,
  InvoiceDto,
  OrganizationDto,
  OrganizationExtendedDto,
  OrganizationWithOwnerDto,
  UserDto,
  UserRoleBindingDto,
  UserRoleDto,
} from '@/castapi';
import { uploadImage } from '@/store/modules/common/files';
import { AppLogger } from '@/logger';
import type { ISearchParams } from '@/castapi/helpers';
import { getAwsApi, getErrorMessage, getInvoicesApi, getOrganizationsApi } from '@/castapi/helpers';
import type { IActionParams, RootGetters, RootState } from '@/store/modules/index';
import type { ITableParamsState } from '@/store/modules/common/tableParamsState';
import TableParamsState from '@/store/modules/common/tableParamsState';

const logger = new AppLogger('adminOrganizations state');

const tableParams = new TableParamsState();

interface IAdminOrganizationsState extends ITableParamsState {
  organizations: OrganizationWithOwnerDto[];
  organizationsLoading: boolean;
  organizationsLoadError?: string;
  currentOrganizationData?: OrganizationExtendedDto;
  currentOrganizationLoading: boolean;
  currentOrganizationLoadError?: string;
  invoicesList: InvoiceDto[];
  invoicesLoading: boolean;
  invoicesLoadError?: string;
  organizationUpdating?: number;
  organizationUpdateError?: string;
  accountCreating: boolean;
  accountCreationError?: string;
}

const initialState = (): IAdminOrganizationsState => ({
  ...tableParams.state(),
  organizations: [],
  organizationsLoading: false,
  organizationsLoadError: undefined,
  currentOrganizationData: undefined,
  currentOrganizationLoading: false,
  currentOrganizationLoadError: undefined,
  invoicesList: [],
  invoicesLoading: false,
  invoicesLoadError: undefined,
  organizationUpdating: undefined,
  organizationUpdateError: undefined,
  accountCreating: false,
  accountCreationError: undefined,
});

interface Getters {
  currentOrganization?: OrganizationDto;
  currentOrganizationAddresses: AddressDto[];
  currentOrganizationMainUserRole: UserRoleBindingDto & UserRoleDto;
}

type ActionParams = IActionParams<IAdminOrganizationsState, Getters>;
export default {
  namespaced: true,
  state: initialState,
  mutations: {
    ...tableParams.mutations,
    ORGANIZATIONS_LOADING(state: IAdminOrganizationsState): void {
      state.organizationsLoading = true;
      state.organizationsLoadError = undefined;
    },
    ORGANIZATIONS_LOADED(state: IAdminOrganizationsState, organizations: OrganizationWithOwnerDto[]): void {
      state.organizations = organizations;
      state.organizationsLoading = false;
    },
    ORGANIZATIONS_LOAD_ERROR(state: IAdminOrganizationsState, payload: Error): void {
      state.organizationsLoading = false;
      state.organizationsLoadError = getErrorMessage(payload);
    },
    CURRENT_ORGANIZATION_LOADING(state: IAdminOrganizationsState): void {
      state.currentOrganizationData = undefined;
      state.currentOrganizationLoading = true;
      state.currentOrganizationData = undefined;
      state.currentOrganizationLoadError = undefined;
    },
    CURRENT_ORGANIZATION_LOADED(state: IAdminOrganizationsState, payload: OrganizationExtendedDto): void {
      state.currentOrganizationData = payload;
      state.currentOrganizationLoading = false;
    },
    CURRENT_LOAD_ERROR(state: IAdminOrganizationsState, payload: Error): void {
      state.currentOrganizationLoadError = getErrorMessage(payload);
      state.currentOrganizationLoading = false;
    },
    ORGANIZATION_UPDATING(state: IAdminOrganizationsState, organizationId: number): void {
      state.organizationUpdating = organizationId;
      state.organizationUpdateError = undefined;
    },
    ORGANIZATION_UPDATED(state: IAdminOrganizationsState): void {
      state.organizationUpdating = undefined;
    },
    ORGANIZATION_UPDATE_ERROR(state: IAdminOrganizationsState, payload: Error): void {
      state.organizationUpdateError = getErrorMessage(payload);
      state.organizationUpdating = undefined;
    },
    ORGANIZATION_DELETED(state: IAdminOrganizationsState): void {
      state.organizationUpdating = undefined;
    },
    INVOICES_LOADING(state: IAdminOrganizationsState): void {
      state.invoicesLoading = true;
      state.invoicesLoadError = undefined;
    },
    INVOICES_LOAD_ERROR(state: IAdminOrganizationsState, payload: Error): void {
      state.invoicesLoading = false;
      state.invoicesLoadError = getErrorMessage(payload);
    },
    INVOICES_LOADED(state: IAdminOrganizationsState, invoices: InvoiceDto[]): void {
      state.invoicesLoading = false;
      state.invoicesList = invoices;
    },
    ACCOUNT_CREATING(state: IAdminOrganizationsState): void {
      state.accountCreationError = undefined;
      state.accountCreating = true;
    },
    ACCOUNT_CREATED(state: IAdminOrganizationsState): void {
      state.accountCreating = false;
    },
    ACCOUNT_CREATION_ERROR(state: IAdminOrganizationsState, payload: Error): void {
      state.accountCreating = false;
      state.accountCreationError = getErrorMessage(payload);
    },
    RESET_STATE(state: IAdminOrganizationsState): void {
      Object.assign(state, initialState());
    },
    RESET_EDITOR_STATE(state: IAdminOrganizationsState): void {
      state.currentOrganizationData = undefined;
      state.currentOrganizationLoading = false;
      state.currentOrganizationLoadError = undefined;
      state.invoicesList = [];
      state.invoicesLoading = false;
      state.invoicesLoadError = undefined;
      state.organizationUpdating = undefined;
      state.organizationUpdateError = undefined;
      state.accountCreating = false;
      state.accountCreationError = undefined;
    },
    CLEAR_ORGANIZATION(state: IAdminOrganizationsState): void {
      state.currentOrganizationData = undefined;
    },
  },
  actions: {
    ...tableParams.actions,
    resetState({ commit }: ActionParams): void {
      commit('RESET_STATE');
    },
    resetEditorState({ commit }: ActionParams): void {
      commit('RESET_EDITOR_STATE');
    },
    async loadOrganizations({ commit, rootGetters }: ActionParams, searchParams: ISearchParams): Promise<void> {
      const { limit, offset, searchText, sortBy, sortDesc, includeOrganizationId } = searchParams || {};
      const isEduMode = rootGetters['adminEdu/isEduMode'];
      commit('ORGANIZATIONS_LOADING', { limit, offset, searchText });
      try {
        const response = await getOrganizationsApi(
          rootGetters['login/accessToken'],
        ).organizationsControllerGetAllOrganizations(
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
          includeOrganizationId,
          isEduMode,
        );
        commit('ORGANIZATIONS_LOADED', response.data);
      } catch (error) {
        commit('ORGANIZATIONS_LOAD_ERROR', error);
        logger.captureStoreError('loadOrganizations', error, {
          limit,
          offset,
          searchText,
          sortBy,
          sortDesc,
          includeOrganizationId,
          isEduMode,
        });
      }
    },

    async getOrganizationData({ commit, rootGetters }: ActionParams, organizationId: number): Promise<void> {
      commit('CURRENT_ORGANIZATION_LOADING');
      try {
        const response = await getOrganizationsApi(
          rootGetters['login/accessToken'],
        ).organizationsControllerGetOrganizationWithUserRoles(organizationId);
        commit('CURRENT_ORGANIZATION_LOADED', response.data);
      } catch (error) {
        commit('CURRENT_LOAD_ERROR', error);
        logger.captureStoreError('getOrganizationData', error, { organizationId });
      }
    },

    async updateOrganization(
      { commit, dispatch, rootGetters }: ActionParams,
      { organizationData, avatarImage }: { organizationData: OrganizationDto; avatarImage: string },
    ): Promise<void> {
      commit('ORGANIZATION_UPDATING', organizationData.organizationId);
      try {
        const organizationAvatar = avatarImage
          ? await uploadImage(
              avatarImage,
              `organization-avatars/${organizationData.organizationId}`,
              getAwsApi(rootGetters['login/accessToken']),
            )
          : null;
        let updateBody = { ...organizationData };
        if (avatarImage !== undefined) {
          updateBody = { ...updateBody, organizationAvatar: organizationAvatar || '' };
        }
        await getOrganizationsApi(rootGetters['login/accessToken']).organizationsControllerUpdateOrganization(
          updateBody,
        );
        commit('ORGANIZATION_UPDATED');
        await dispatch('getOrganizationData', organizationData.organizationId);
      } catch (error) {
        commit('ORGANIZATION_UPDATE_ERROR', error);
        logger.captureStoreError('updateOrganization', error, { organizationData, avatarImage });
      }
    },

    async loadInvoices({ commit, getters, rootGetters }: ActionParams): Promise<void> {
      commit('INVOICES_LOADING');
      try {
        if (!getters.currentOrganization) {
          throw new Error('Organization is not selected');
        }
        const response = await getInvoicesApi(
          rootGetters['login/accessToken'],
        ).invoicesControllerGetOrganizationInvoices(getters.currentOrganization.organizationId);
        commit('INVOICES_LOADED', response.data);
      } catch (error) {
        commit('INVOICES_LOAD_ERROR', error);
        logger.captureStoreError('loadInvoices', error, {
          organizationId: getters.currentOrganization?.organizationId,
        });
      }
    },

    async deleteOrganization({ commit, rootGetters }: ActionParams, organizationId: number): Promise<void> {
      commit('ORGANIZATION_UPDATING', organizationId);
      try {
        await getOrganizationsApi(rootGetters['login/accessToken']).organizationsControllerDeleteOrganization({
          organizationId,
        });
        commit('ORGANIZATION_DELETED');
      } catch (error) {
        commit('ORGANIZATION_UPDATE_ERROR', error);
        logger.captureStoreError('deleteOrganization', error, { organizationId });
      }
    },

    async createAccount({ commit, rootGetters }: ActionParams, createAccountDto: CreateAccountDto): Promise<void> {
      commit('ACCOUNT_CREATING');
      try {
        const response = await getOrganizationsApi(
          rootGetters['login/accessToken'],
        ).organizationsControllerCreateAccountByAdmin(createAccountDto);
        // `response.data` (organizationId) is using in mutation, but not in action
        commit('ACCOUNT_CREATED', response.data);
      } catch (error) {
        commit('ACCOUNT_CREATION_ERROR', error);
        logger.captureStoreError('createAccount', error);
      }
    },
  },
  getters: {
    ...tableParams.getters,
    organizations: (state: IAdminOrganizationsState): OrganizationWithOwnerDto[] => state.organizations,
    organizationsLoading: (state: IAdminOrganizationsState): boolean => state.organizationsLoading,
    organizationsLoadError: (state: IAdminOrganizationsState): undefined | string => state.organizationsLoadError,
    currentOrganizationData: (state: IAdminOrganizationsState): undefined | OrganizationExtendedDto =>
      state.currentOrganizationData,
    currentOrganization: (state: IAdminOrganizationsState): undefined | OrganizationDto =>
      state.currentOrganizationData?.organization,
    currentOrganizationId: (state: IAdminOrganizationsState): undefined | number =>
      state.currentOrganizationData?.organization.organizationId,
    currentOrganizationUsers: (state: IAdminOrganizationsState): UserDto[] =>
      state.currentOrganizationData?.users || [],
    currentOrganizationMembers: (state: IAdminOrganizationsState): UserDto[] =>
      state.currentOrganizationData?.users?.filter(
        u => state.currentOrganizationData?.rolesBinding?.find(rb => rb.userRef === u.userId)?.userRoleRef === 3,
      ) || [],
    currentOrganizationMainUserRole: (
      state: IAdminOrganizationsState,
      _getters: Getters,
      _rootState: RootState,
      rootGetters: RootGetters,
    ): undefined | (UserRoleBindingDto & UserRoleDto) => {
      let priority = 0;
      let mainUserRole;
      state.currentOrganizationData?.rolesBinding?.forEach(binding => {
        const roleType = rootGetters['dictionary/roleTypeGetter'](binding.userRoleRef);
        if (roleType.priority >= priority) {
          mainUserRole = { ...binding, ...roleType };
          priority = roleType.priority;
        }
      });
      return mainUserRole;
    },
    // Plan: Need to fix. A lot of cases when main user is not found. Perhaps, users or rolesBinding is not loaded
    currentOrganizationMainUser: (state: IAdminOrganizationsState, getters: Getters): UserDto => {
      const found = state.currentOrganizationData?.users?.find(
        (user: UserDto) => user.userId === getters.currentOrganizationMainUserRole?.userRef,
      );
      if (!found) {
        return {} as UserDto; // temp fix
        // throw new Error('Main user not found');
      }
      return found;
    },
    currentOrganizationLoading: (state: IAdminOrganizationsState): boolean => state.currentOrganizationLoading,
    currentOrganizationLoadError: (state: IAdminOrganizationsState): undefined | string =>
      state.currentOrganizationLoadError,
    currentOrganizationAddresses: (state: IAdminOrganizationsState): undefined | AddressDto[] =>
      state.currentOrganizationData?.addresses,
    currentOrganizationShippingAddresses: (_state: IAdminOrganizationsState, getters: Getters): AddressDto[] =>
      getters.currentOrganizationAddresses?.filter((address: AddressDto) => !address.isBilling) || [],
    currentOrganizationBillingAddresses: (_state: IAdminOrganizationsState, getters: Getters): AddressDto[] =>
      getters.currentOrganizationAddresses?.filter((address: AddressDto) => address.isBilling) || [],
    invoices: (state: IAdminOrganizationsState): InvoiceDto[] => state.invoicesList,
    invoicesLoading: (state: IAdminOrganizationsState): boolean => state.invoicesLoading,
    invoicesLoadError: (state: IAdminOrganizationsState): undefined | string => state.invoicesLoadError,
    // organizationUpdating: (state:IAdminOrganizationsState) => state.organizationUpdating,
    organizationUpdateError: (state: IAdminOrganizationsState): undefined | string => state.organizationUpdateError,
    accountCreationError: (state: IAdminOrganizationsState): undefined | string => state.accountCreationError,
    accountCreating: (state: IAdminOrganizationsState): boolean => state.accountCreating,
  },
};
